import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, CheckCircle, Play, Download, Share2, X } from 'lucide-react';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

export const ServiceDetailPage = ({ service, onBack }) => {
  const navigate = useNavigate();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [videoError, setVideoError] = useState(false);
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [heroContentRef, heroContentVisible] = useScrollAnimation({ delay: 200 });
  const [heroImageRef, heroImageVisible] = useScrollAnimation({ delay: 400 });
  const [contentRef, contentVisible] = useScrollAnimation({ delay: 600 });
  const [galleryRef, galleryVisible] = useScrollAnimation({ delay: 800 });
  const [featuresRef, featuresVisible] = useScrollAnimation({ delay: 1000 });

  const openVideoModal = (videoUrl) => {
    console.log('Opening video:', videoUrl);
    setCurrentVideo(videoUrl);
    setVideoError(false);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setCurrentVideo('');
    setVideoError(false);
  };

  const handleBack = () => {
    navigate('/');
  };

  // Dummy data for service details
  const serviceDetails = {
    "Smart Street Lighting": {
      overview: "Our Smart Street Lighting solution revolutionizes urban illumination with IoT-enabled LED technology. This comprehensive system delivers unprecedented energy savings while enhancing safety and reducing maintenance costs.",
      keyBenefits: [
        "70% reduction in energy consumption",
        "Real-time monitoring and control",
        "Automated dimming based on traffic patterns",
        "Cloud-based management system",
        "Weather-resistant design",
        "Long-lasting LED technology"
      ],
      technicalSpecs: {
        "Power Consumption": "50-150W per unit",
        "Lifespan": "50,000+ hours",
        "Control Range": "Up to 1km radius",
        "Weather Rating": "IP65",
        "Communication": "4G/WiFi enabled",
        "Installation": "Pole-mounted or wall-mounted"
      },
      applications: [
        "Highway lighting",
        "City street illumination",
        "Park and recreational areas",
        "Commercial districts",
        "Residential neighborhoods",
        "Industrial zones"
      ],
      images: [       
        process.env.PUBLIC_URL+"/Website_Work/01_Smart_Lighting/Customised-SHUFFLEs-creates-connected-urban-public-space-for-McGill-University.jpg",
        process.env.PUBLIC_URL+"/Website_Work/01_Smart_Lighting/ROAD-A9-Noord-Holland-Netherlands-IZYLUM-EXEDRA-Schreder-HIM_0502.jpg",
        process.env.PUBLIC_URL+"/Website_Work/01_Smart_Lighting/A9-Noord-Holland-Netherlands-IZYLUM-EXEDRA-Schreder-11.jpg"
      ],
      videos: [
        process.env.PUBLIC_URL+"/Website_Work/01_Smart_Lighting/SH-02.mp4.mp4",
        process.env.PUBLIC_URL+"/Website_Work/01_Smart_Lighting/Schréder_VisioLum.mp4.mp4",
        process.env.PUBLIC_URL+"/Website_Work/01_Smart_Lighting/FLEXIA_enhanced_block#1.mp4.mp4"
      ],
      demoVideo: process.env.PUBLIC_URL+"/Website_Work/01_Smart_Lighting/SH-02.mp4.mp4"

    },
    "Traffic Enforcement Systems": {
      overview: "Advanced LIDAR-based speed enforcement system with 75-meter operational range. Our solution provides accurate, reliable traffic monitoring across multiple lanes with real-time violation detection and reporting.",
      keyBenefits: [
        "75-meter detection range",
        "Multi-lane monitoring capability",
        "Real-time violation reporting",
        "Mobile deployment options",
        "Weather-resistant operation",
        "Integration with traffic management systems"
      ],
      technicalSpecs: {
        "Detection Range": "75 meters",
        "Accuracy": "±1 km/h",
        "Lanes Monitored": "Up to 6 lanes",
        "Weather Rating": "IP67",
        "Power Source": "Solar/Battery/Mains",
        "Data Storage": "Cloud + Local backup"
      },
      applications: [
        "Highway speed enforcement",
        "School zone monitoring",
        "Construction zone safety",
        "Urban traffic management",
        "Bridge and tunnel monitoring",
        "Mobile enforcement units"
      ],
      images: [
        process.env.PUBLIC_URL+"/Website_Work/02_Traffic_Enforcement/Slide1.JPG",
        // process.env.PUBLIC_URL+"/Website_Work/02_Traffic_Enforcement/vitronic-bilderverarbeitung.png"
      ],
      videos: [
        process.env.PUBLIC_URL+"/Website_Work/02_Traffic_Enforcement/Web_Enclosures_08_CDH_CCH.mp4"
      ],
      demoVideo: process.env.PUBLIC_URL+"/Website_Work/02_Traffic_Enforcement/Web_Enclosures_08_CDH_CCH.mp4"
    },
    "City Monitoring Systems": {
      overview: "360-degree AI-powered surveillance system providing comprehensive city monitoring. Our solution offers real-time data transmission, intelligent analytics, and seamless integration with control rooms for optimized urban management.",
      keyBenefits: [
        "360-degree panoramic coverage",
        "AI-powered analytics",
        "Real-time data transmission",
        "Traffic optimization algorithms",
        "Incident detection and alerting",
        "Scalable cloud infrastructure"
      ],
      technicalSpecs: {
        "Camera Resolution": "4K Ultra HD",
        "Field of View": "360° panoramic",
        "Night Vision": "IR illumination",
        "Storage": "Cloud + Edge computing",
        "Analytics": "AI-powered object detection",
        "Integration": "API-based connectivity"
      },
      applications: [
        "Traffic flow monitoring",
        "Security surveillance",
        "Incident detection",
        "Crowd management",
        "Environmental monitoring",
        "Smart city analytics"
      ],
      images: [
        process.env.PUBLIC_URL+"/Website_Work/03_City_Monitoring/01_Final_one.jpg",
        process.env.PUBLIC_URL+"/Website_Work/03_City_Monitoring/420c23ed-18c2-4a9d-bfba-6b229dd186a5_distance-tolling-tt-installation-leadcard-1920x1280.jpg",
        process.env.PUBLIC_URL+"/Website_Work/03_City_Monitoring/6ff3bda7-a7bc-458f-b9ad-10e8e82ae81a_distance-tolling-tt-teaser-1920x1280.jpg"
      ],
      videos: [
        process.env.PUBLIC_URL+"/Website_Work/03_City_Monitoring/distance-tolling-tt-tollbridge-video-1920x1080.mp4",
        process.env.PUBLIC_URL+"/Website_Work/03_City_Monitoring/City_Tolling_VideoB_Low.mp4",
        process.env.PUBLIC_URL+"/Website_Work/03_City_Monitoring/Mobile_Seatbelt_05.mp4"
      ],
      demoVideo: process.env.PUBLIC_URL+"/Website_Work/03_City_Monitoring/Web_6Lanes_02.mp4"
    },
    "Solar Solutions": {
      overview: "Eco-friendly solar-powered lighting solutions including street lights, benches, and bollards. Our sustainable approach reduces carbon footprint while providing reliable, maintenance-free illumination for urban environments.",
      keyBenefits: [
        "100% renewable energy",
        "Zero carbon emissions",
        "Low maintenance requirements",
        "Weather-resistant design",
        "Automatic dusk-to-dawn operation",
        "Modular installation system"
      ],
      technicalSpecs: {
        "Solar Panel": "Monocrystalline silicon",
        "Battery": "Lithium-ion",
        "LED Power": "30-100W",
        "Autonomy": "3-5 days backup",
        "Weather Rating": "IP65",
        "Warranty": "5 years comprehensive"
      },
      applications: [
        "Solar street lighting",
        "Solar-powered benches",
        "Solar bollards",
        "Park illumination",
        "Remote area lighting",
        "Emergency backup systems"
      ],
      images: [
        process.env.PUBLIC_URL+"/Website_Work/04_Solar_street_light/solar_solution_final.jpg",
        process.env.PUBLIC_URL+"/Website_Work/04_Solar_street_light/Screenshot_2025-09-06_123258.png"
      ],
      videos: [
        process.env.PUBLIC_URL+"/Website_Work/04_Solar_street_light/PREVIA_-_VERTICALIS_-_PROTOS_-_Banner_WEB.mp4",
        process.env.PUBLIC_URL+"/Website_Work/04_Solar_street_light/ForOurPlanetSolarSolution.mp4"
      ],
      demoVideo:process.env.PUBLIC_URL+"/Website_Work/04_Solar_street_light/PREVIA_-_VERTICALIS_-_PROTOS_-_Banner_WEB.mp4",

    },
    "Facade Lighting": {
      overview: "Energy-efficient LED technology for architectural illumination of monuments, fountains, and bridges. Our solution includes projection mapping capabilities for cultural events and tourism enhancement, creating stunning visual experiences.",
      keyBenefits: [
        "Energy-efficient LED technology",
        "Projection mapping capabilities",
        "Color-changing RGB systems",
        "Weather-resistant fixtures",
        "Remote control and scheduling",
        "Cultural event enhancement"
      ],
      technicalSpecs: {
        "LED Technology": "High-power RGB LEDs",
        "Color Range": "16.7 million colors",
        "Control System": "DMX512 protocol",
        "Projection": "High-lumen projectors",
        "Weather Rating": "IP65",
        "Control": "Wireless remote + app"
      },
      applications: [
        "Monument illumination",
        "Fountain light shows",
        "Bridge lighting",
        "Building facade lighting",
        "Cultural event displays",
        "Tourism enhancement"
      ],
      images: [
        process.env.PUBLIC_URL+"/Website_Work/05_Facade_lighting/02.jpg",
        process.env.PUBLIC_URL+"/Website_Work/05_Facade_lighting/03.jpg"
      ],
      videos: [],
      // demoVideo: process.env.PUBLIC_URL+"/Website_Work/05_Facade_lighting/video2.mp4"
    },
    "Smart Solar Pole": {
      overview: "Revolutionary smart poles that integrate solar power, LED lighting, and IoT sensors in a single solution. Our Smart Solar Pole provides wireless charging capabilities, environmental monitoring, and seamless smart city connectivity for modern urban infrastructure.",
      keyBenefits: [
        "Integrated solar power generation",
        "Wireless charging stations",
        "Environmental monitoring sensors",
        "Smart city connectivity",
        "Weather-resistant design",
        "Modular installation system"
      ],
      technicalSpecs: {
        "Solar Panel": "Monocrystalline 300W",
        "Battery Capacity": "10kWh lithium-ion",
        "LED Power": "100-200W",
        "Wireless Charging": "Qi-compatible",
        "Sensors": "Air quality, temperature, humidity",
        "Connectivity": "4G/WiFi/LoRaWAN"
      },
      applications: [
        "Smart city infrastructure",
        "Public charging stations",
        "Environmental monitoring",
        "Emergency communication",
        "Tourist information points",
        "Urban WiFi hotspots"
      ],
      images: [       
        process.env.PUBLIC_URL+"/Website_Work/05_Smart_Solar_Pole/03.png"
      ],
      videos: [
        process.env.PUBLIC_URL+"/Website_Work/05_Smart_Solar_Pole/WhatsApp_Video_2025-02-05_at_3.08.29_PM.mp4"
      ],
      demoVideo: process.env.PUBLIC_URL+"/Website_Work/05_Smart_Solar_Pole/WhatsApp_Video_2025-02-05_at_3.08.29_PM.mp4"
    },
    "Light Bar": {
      overview: "High-performance LED light bars designed for vehicles and infrastructure applications. Our Light Bar solutions provide superior illumination with customizable configurations, weather-resistant design, and energy-efficient operation for various industrial and commercial uses.",
      keyBenefits: [
        "High-performance LED technology",
        "Customizable configurations",
        "Weather-resistant construction",
        "Energy-efficient operation",
        "Easy installation and maintenance",
        "Multiple mounting options"
      ],
      technicalSpecs: {
        "LED Type": "High-power COB LEDs",
        "Power Range": "50W - 500W",
        "Luminous Flux": "Up to 50,000 lumens",
        "Beam Angle": "15° - 120° adjustable",
        "Weather Rating": "IP67",
        "Operating Temperature": "-40°C to +60°C"
      },
      applications: [
        "Emergency vehicles",
        "Construction equipment",
        "Marine vessels",
        "Industrial facilities",
        "Airport runways",
        "Highway maintenance"
      ],
      images: [
        process.env.PUBLIC_URL+"/Website_Work/06_Light_Bar/Light_Bar.png"
      ],
      videos: [
        process.env.PUBLIC_URL+"/Website_Work/06_Light_Bar/Libya_03.mp4"
      ],
      demoVideo: process.env.PUBLIC_URL+"/Website_Work/06_Light_Bar/Libya_03.mp4"
    },
    "DC Fast EV Charger": {
      overview: "Advanced DC fast charging stations for electric vehicles with smart grid integration. Our solution supports multiple charging standards, features integrated payment systems, and provides remote monitoring capabilities for efficient EV infrastructure management.",
      keyBenefits: [
        "Ultra-fast DC charging",
        "Smart grid integration",
        "Multiple charging standards",
        "Integrated payment systems",
        "Remote monitoring capabilities",
        "Scalable infrastructure"
      ],
      technicalSpecs: {
        "Charging Power": "50kW - 350kW",
        "Standards": "CCS, CHAdeMO, Tesla",
        "Efficiency": "95%+ charging efficiency",
        "Payment": "RFID, mobile app, credit card",
        "Monitoring": "Real-time cloud dashboard",
        "Installation": "Ground or wall-mounted"
      },
      applications: [
        "Highway charging stations",
        "Urban charging hubs",
        "Fleet charging depots",
        "Shopping centers",
        "Corporate facilities",
        "Public transportation hubs"
      ],
      images: [
        process.env.PUBLIC_URL+"/Website_Work/07_DC_Fast_EV_Charger/01_Final_one.jpeg",
        process.env.PUBLIC_URL+"/Website_Work/07_DC_Fast_EV_Charger/02.png",
        process.env.PUBLIC_URL+"/Website_Work/07_DC_Fast_EV_Charger/03.jpg"
      ],
      videos: [
        // process.env.PUBLIC_URL+"/Website_Work/07_DC_Fast_EV_Charger/01_Final_one.jpeg"
      ],
      // demoVideo: process.env.PUBLIC_URL+"/Website_Work/07_DC_Fast_EV_Charger/01_Final_one.jpeg"
    },
    "Battery Power": {
      overview: "Advanced battery storage solutions for renewable energy integration and backup power for critical traffic infrastructure. Our comprehensive battery power systems feature smart energy management, grid stabilization capabilities, and seamless integration with renewable energy sources.",
      keyBenefits: [
        "Renewable energy integration",
        "24/7 reliable backup power",
        "Grid stabilization capabilities",
        "Emergency power systems",
        "Smart energy management",
        "Scalable storage solutions"
      ],
      technicalSpecs: {
        "Battery Type": "Lithium-ion",
        "Capacity": "10kWh - 100kWh",
        "Efficiency": "95%+ round-trip",
        "Lifespan": "10+ years",
        "Temperature Range": "-20°C to +60°C",
        "Management": "Smart BMS integration"
      },
      applications: [
        "Solar energy storage",
        "Grid backup systems",
        "Emergency power",
        "Peak shaving",
        "Microgrid solutions",
        "Critical infrastructure backup"
      ],
      images: [
        process.env.PUBLIC_URL+"/Website_Work/09_Battary_power/battery1.png",        // "https://images.unsplash.com/photo-1599305445771-3845a82a4a8a?w=800&h=600&fit=crop&crop=center"
        process.env.PUBLIC_URL+"/Website_Work/09_Battary_power/battery2.jpeg",       // "https://images.unsplash.com/photo-1599305445771-3845a82a4a8a?w=800&h=600&fit=crop&crop=center"
        process.env.PUBLIC_URL+"/Website_Work/09_Battary_power/battery3.png",       // "https://images.unsplash.com/photo-1599305445771-3845a82a4a8a?w=800&h=600&fit=crop&crop=center"
      ],
      videos: [
        // Add video URLs when available
      ],
      demoVideo: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop&crop=center"
    }
  };

  const details = serviceDetails[service.title] || serviceDetails["Smart Street Lighting"];
  const hasVideos = Boolean(
    details.demoVideo || (details.videos && details.videos.length > 0)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <div ref={headerRef} className={`bg-white shadow-sm border-b ${createScrollAnimationClasses('fadeInDown', headerVisible)}`}>
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
              <Button 
                variant="ghost" 
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 text-sm sm:text-base"
              >
                <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
                <span className="hidden xs:inline">Back to Services</span>
                <span className="xs:hidden">Back</span>
              </Button>
              <div className="hidden sm:block h-6 w-px bg-gray-300"></div>
              <Badge className="bg-blue-50 text-blue-700 px-2 py-1 text-xs sm:text-sm">
                <span className="hidden sm:inline">{service.title}</span>
                <span className="sm:hidden">{service.title.split(' ')[0]}</span>
              </Badge>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end">
              <Button variant="outline" size="sm" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                <Share2 size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Share</span>
              </Button>
              <Button variant="outline" size="sm" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
                <Download size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Brochure</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8 sm:mb-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content - Slides from Left */}
            <div ref={heroContentRef} className={`order-2 lg:order-1 ${createScrollAnimationClasses('fadeInLeft', heroContentVisible)}`}>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                {service.title}
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                {details.overview}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base">
                  Request Quote
                </Button>
                {details.demoVideo && (
                  <Button 
                    variant="outline" 
                    className="px-6 sm:px-8 py-2 sm:py-3 flex items-center gap-2 text-sm sm:text-base"
                    onClick={() => openVideoModal(details.demoVideo)}
                  >
                    <Play size={16} className="sm:w-5 sm:h-5" />
                    Watch Demo
                  </Button>
                )}
              </div>
            </div>
            
            {/* Right Image - Slides from Right */}
            <div ref={heroImageRef} className={`order-1 lg:order-2 relative ${createScrollAnimationClasses('fadeInRight', heroImageVisible)}`}>
              <img 
                src={service.image}
                alt={service.title}
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl sm:rounded-2xl"></div>
            </div>
          </div>
        </div>

        {/* Key Benefits */}
        <div ref={featuresRef} className={`mb-8 sm:mb-12 ${createScrollAnimationClasses('fadeInUp', featuresVisible)}`}>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">Key Benefits</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {details.keyBenefits.map((benefit, index) => (
              <Card key={index} className="p-4 sm:p-6 border-0 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="bg-blue-50 rounded-full p-2 flex-shrink-0">
                    <CheckCircle size={18} className="sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 font-medium">{benefit}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">Technical Specifications</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Object.entries(details.technicalSpecs).map(([key, value], index) => (
              <Card key={index} className="p-4 sm:p-6 border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">{key}</h3>
                <p className="text-blue-600 font-medium text-sm sm:text-base">{value}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Applications */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Applications</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {details.applications.map((application, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <p className="text-gray-700">{application}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Image Gallery */}
        {hasVideos && (
        <div ref={galleryRef} className={`mb-12 ${createScrollAnimationClasses('fadeInUp', galleryVisible)}`}>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Project Gallery</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {details.images.map((image, index) => (
              <div key={index} className="relative group cursor-pointer">
                <img 
                  src={image}
                  alt={`${service.title} - Image ${index + 1}`}
                  className="w-full h-64 object-cover rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div 
                    className="bg-white/90 rounded-full p-3 cursor-pointer hover:bg-white transition-colors"
                    onClick={() => openVideoModal(details.videos && details.videos[index] ? details.videos[index] : details.demoVideo)}
                  >
                    <Play size={24} className="text-blue-600" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your City?</h2>
          <p className="text-xl mb-8 opacity-90">
            Contact us today to learn more about implementing {service.title} in your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3">
              Get Free Consultation
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3">
              Download Brochure
            </Button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden">
            {/* Close Button */}
            <button
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
            >
              <X size={24} className="text-gray-600" />
            </button>
            
            {/* Video Player */}
            <div className="aspect-video">
              {!videoError ? (
                <video
                  src={currentVideo}
                  controls
                  autoPlay
                  className="w-full h-full object-cover"
                  preload="metadata"
                  onEnded={closeVideoModal}
                  onError={(e) => {
                    console.error('Video error:', e);
                    console.error('Video src:', currentVideo);
                    console.error('Error details:', e.target.error);
                    setVideoError(true);
                  }}
                  onLoadStart={() => console.log('Video loading started:', currentVideo)}
                  onCanPlay={() => console.log('Video can play:', currentVideo)}
                  onLoadedData={() => console.log('Video data loaded:', currentVideo)}
                  onProgress={() => console.log('Video loading progress')}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center text-gray-500">
                  <div className="text-center p-8">
                    <div className="text-6xl mb-4">📹</div>
                    <h3 className="text-xl font-semibold mb-2">Video Unavailable</h3>
                    <p className="text-sm mb-4">The video file could not be loaded.</p>
                    <div className="space-y-2">
                      <Button 
                        onClick={() => setVideoError(false)}
                        className="bg-blue-600 hover:bg-blue-700 text-white mr-2"
                      >
                        Try Again
                      </Button>
                      <Button 
                        onClick={closeVideoModal}
                        variant="outline"
                        className="border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Video Info */}
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {service.title} - Demo Video
              </h3>
              <p className="text-gray-600">
                Watch this demonstration to see how our {service.title} solution works in real-world applications.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
